import * as y from 'yup';
import uniqueId from 'lodash/uniqueId';
import { validateCNPJ, validateCPF, validateDate, validatePhone } from './validation';
import { maskMoney, unmaskMoney } from './masks';
import { DateTime } from 'luxon';

/**
 *
 * @param {Object} options
 * @param {boolean} [options.required=false] - Whether the field is required
 * @param {number} [options.min] - Minimum value in cents (e.g., 1000 for R$10,00)
 * @param {number} [options.max] - Maximum value in cents (e.g., 500000 for R$5.000,00)
 * @param {boolean} [options.allowZeros=false] - Whether to allow zero values
 * @returns
 */
export function createMoneySchema(options) {
  const validator = y.string().test('is-money', (value, ctx) => {
    const input = String(value || '').trim();
    if (!input) {
      if (options.required) {
        return ctx.createError({ message: 'Campo obrigatório' });
      }

      return true;
    }

    if (options.allowZeros && !options.required) {
      const val = unmaskMoney(input);
      if (val === 0) {
        return true;
      }
    }

    const [val, cents] = input
      .trim()
      .split(',')
      .map((part) => part.replace(/\./g, ''));

    let numericTotalCents = 0;

    if (cents) {
      const numericCents = Number(cents);
      if (isNaN(numericCents) || numericCents < 0 || numericCents > 99)
        return ctx.createError({ message: 'Valor inválido' });

      numericTotalCents += numericCents;
    }

    if (val) {
      const numericVal = Number(val);
      if (isNaN(numericVal)) return ctx.createError({ message: 'Valor inválido' });

      numericTotalCents += numericVal * 100;
    }

    if (options?.min) {
      const minCents = options.min;
      if (numericTotalCents < minCents)
        return ctx.createError({ message: `Valor mínimo de ${maskMoney(options.min)}` });
    }

    if (options?.max) {
      const maxCents = options.max;
      if (numericTotalCents > maxCents)
        return ctx.createError({ message: `Valor máximo de ${maskMoney(options.max)}` });
    }

    return true;
  });

  return validator;
}

/**
 *
 * @param {'both' | 'cpf' | 'cnpj'} type
 */
export function createCPFOrCNPJSchema(type = 'both') {
  return y
    .string()
    .required('Campo obrigatório')
    .test('cpfOrCnpj', (value, ctx) => {
      const cleanedValue = (value || '').replace(/\D/g, '');

      if (type === 'cpf' || (cleanedValue.length <= 11 && type === 'both')) {
        const valid = validateCPF(cleanedValue);
        if (!valid) {
          return ctx.createError({ message: 'CPF inválido' });
        }
        return true;
      }

      const valid = validateCNPJ(cleanedValue);
      if (!valid) {
        return ctx.createError({ message: 'CNPJ inválido' });
      }
      return true;
    });
}

export function createDocumentTypeSchema() {
  return y
    .string()
    .required('Campo obrigatório')
    .test('documentType', (value, ctx) => {
      const documentType = ctx.parent.documentType === ctx.parent._documentTypeCpf ? 'cpf' : 'cnpj';

      if (documentType === 'cpf') {
        const valid = validateCPF(value);
        if (!valid) {
          return ctx.createError({ message: 'CPF inválido' });
        }
        return true;
      }

      const valid = validateCNPJ(value);
      if (!valid) {
        return ctx.createError({ message: 'CNPJ inválido' });
      }
      return true;
    });
}

/**
 * Creates a custom Yup validation schema with optional required validation
 * @param {Object} options - Configuration options for the schema
 * @param {Function} [options.validate] - Custom validation function that returns a boolean
 * @param {boolean} [options.required=false] - Whether the field is required
 * @param {Object} [options.messages] - Custom error messages
 * @param {string} [options.messages.invalid='Campo inválido'] - Error message for invalid input
 * @param {string} [options.messages.required='Campo obrigatório'] - Error message for required field
 */
export function createTestSchema(options = {}) {
  const validate = options.validate;
  const required = options.required || false;

  const errorMsgInvalid = options.messages?.invalid || 'Campo inválido';
  const errorMsgRequired = options.messages?.required || 'Campo obrigatório';

  const schemas = [];

  if (required) {
    schemas.push(
      y
        .mixed()
        .test(`test-required-${uniqueId()}`, errorMsgRequired, (v) => {
          return !!v;
        })
        .required(errorMsgRequired),
    );
  }

  if (validate) {
    schemas.push(y.mixed().test(`test-${uniqueId()}`, errorMsgInvalid, validate));
  }

  return schemas.reduce((acc, schema) => acc.concat(schema), y.mixed());
}

/**
 *
 * @param {Object} options
 * @param {string} options.minDate - Minimum date in 'yyyy-MM-dd' format
 * @param {string} options.maxDate - Maximum date in 'yyyy-MM-dd' format
 * @returns
 */
export function createDatePeriodSchema(options) {
  return y
    .string()
    .required('Campo obrigatório')
    .test(`valid-date-${uniqueId()}`, 'Data inválida', (input, ctx) => {
      if (!input || !options || !options?.maxDate || !options?.minDate || input.length < 10) {
        return false;
      }

      const currentDate = DateTime.fromFormat(`${input}`.trim(), 'dd/MM/yyyy');
      if (!currentDate.isValid) return false;

      const minDate = DateTime.fromFormat(options.minDate, 'yyyy-MM-dd');
      const maxDate = DateTime.fromFormat(options.maxDate, 'yyyy-MM-dd');

      if (currentDate < minDate) {
        return ctx.createError({
          message: `A data deve ser igual ou posterior a ${minDate.toFormat('dd/MM/yyyy')}`,
        });
      }

      if (currentDate > maxDate) {
        return ctx.createError({
          message: `A data deve ser igual ou anterior a ${maxDate.toFormat('dd/MM/yyyy')}`,
        });
      }

      return true;
    });
}

export const createBirthDateSchema = () => {
  return y
    .string()
    .required('Campo obrigatório')
    .test(`valid-birthdate-${uniqueId()}`, 'Data inválida', (input, ctx) => {
      const valid = validateDate(input);
      if (!valid) return ctx.createError({ message: 'Data inválida' });

      const date = DateTime.fromFormat(`${input}`.trim(), 'dd/MM/yyyy').startOf('day');
      if (!date.isValid) return ctx.createError({ message: 'Data inválida' });

      const today = DateTime.now().startOf('day');
      const diff = today.diff(date, 'days').days;

      if (diff < 1) {
        return ctx.createError({ message: 'Data inválida' });
      }

      const minDate = DateTime.now().startOf('day').minus({ years: 100 });

      const minDiff = date.diff(minDate, 'days').days;

      if (minDiff < 0) {
        return ctx.createError({ message: 'Data deve ser anterior a 100 anos' });
      }

      return true;
    });
};
